import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Field, Form, Formik } from 'formik';
import { FloatingInput } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { CactusImage } from 'src/assets/images';

import { InfoTray, StatusSpan, FilterCriteriaRow } from 'src/components';
import { Environment } from 'src/Environment';
import { getTerminals } from 'src/services/terminal.service';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import TealiumEvents from 'src/helpers/tealium-events';
import HeaderNav from '../header-nav/header-nav';
import RevampAccordion from 'src/components/revamp-accordion/revamp-accordion';
import RevampAccordionItem from 'src/components/revamp-accordion-item/revamp-accordion-item';
import TerminalDetailsForm from 'src/components/terminal-details-form/terminal-details-form';
import useTechnicalErrorSelfServiceHandler from 'src/hooks/useTechnicalErrorSelfService';
import { Card } from 'src/components';
import { queue } from 'src/helpers/delay';

import 'src/sass/self-service-portal.scss';
import './terminals.scss';

export default function Terminals() {
  const handleTechnicalError = useTechnicalErrorSelfServiceHandler();
  const { merchant } = useContext(MerchantProfileContext);
  const [selectedTerminalType, setSelectedTerminalType] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [filteredTerminalList, setFilteredTerminalList] = useState([]);
  const [terminalFilterOptions, setTerminalFilterOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(false);

  const displayIncrements = 6;
  const [displayedAmount, setDisplayedAmount] = useState(displayIncrements);

  const allTerminalsOption = { type: 'All terminals', id: null };
  const updateTerminalDetailsEnabled = useMemo(
    () => !Environment.current?.disabledFeatures?.updateTerminalDetails,
    [],
  );
  const terminalSelectFilter = () => {
    return (
      <div className="select-filter has-margin-top-3 has-mobile-margin-top-0">
        <Field
          name="terminal"
          type="text"
          placeholder="Terminal"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          dontClearAutocompleteOnClick={true}
          items={[allTerminalsOption, ...terminalFilterOptions]}
          getItemDisplayValue={(item) => item.type}
          filterItemsWithInputValue={true}
          onItemSelect={(item) => {
            TealiumEvents.terminalsFiltersApplied(item.type);

            const selected = terminalFilterOptions.find((terminal) => {
              return terminal.id === item.id;
            });

            if (selected) {
              setDisplayedAmount(displayIncrements);
              setSelectedTerminalType(selected);
              return;
            }

            setSelectedTerminalType('');
          }}
          fieldType="select"
          isDark={true}
          hasNoAfterLine={true}
        />
      </div>
    );
  };

  useEffect(() => {
    TealiumEvents.terminalsPageLoadNewStyle();

    const abortController = new AbortController();
    const retrieveTerminals = async () => {
      setIsLoading(true);
      try {
        const data = await getTerminals(abortController, handleTechnicalError, false, true);

        if (!abortController.signal.aborted) {
          setTerminals(data?.terminalsArray || []);
          setTerminalFilterOptions(data?.terminalTypesArray || []);
          setIsLoading(false);
        }
      } catch (e) {
        setIsLoading(false);
      }
    };
    queue.push(retrieveTerminals);
  }, []);

  useEffect(() => {
    const filteredList = terminals.filter(
      (terminal) =>
        !selectedTerminalType ||
        (!!selectedTerminalType && terminal && terminal.terminalTypeId === selectedTerminalType.id),
    );

    setFilteredTerminalList(filteredList);
  }, [selectedTerminalType, terminals]);

  return (
    <div className="Terminals" data-testid="Terminals">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
        screenName="Terminals"
      >
        <p className="filter-title is-vcentered is-hidden-tablet is-hidden-mobile">
          Filter by:
          <span
            className="item-icon-white is-clickable is-icon-4 icon-filters is-pulled-right"
            onClick={() => setSearch(true)}
          ></span>
        </p>
        <Formik
          initialValues={{ terminal: allTerminalsOption.type }}
          validateOnChange
          validate={() => setSearch(false)}
        >
          {({ setValues }) => {
            return (
              <>
                <FilterCriteriaRow
                  className="is-hidden-tablet"
                  filterCriteria={[
                    {
                      value: selectedTerminalType?.type,
                      clear: () => {
                        setSelectedTerminalType(null);
                        setValues({ terminal: allTerminalsOption.type });
                      },
                    },
                  ]}
                />
                <Form className="filter-form ">{terminalSelectFilter()}</Form>

                <InfoTray selected={search} setSelected={setSearch} title="Filter by">
                  <Form>
                    <FilterCriteriaRow
                      filterCriteria={[
                        {
                          value: selectedTerminalType?.type,
                          clear: () => {
                            setSelectedTerminalType(null);
                            setValues({ terminal: allTerminalsOption.type });
                          },
                        },
                      ]}
                    />
                    {terminalSelectFilter()}
                  </Form>
                </InfoTray>
              </>
            );
          }}
        </Formik>
        <div className="is-hidden-tablet has-margin-top-5">
          {isLoading && (
            <table className="table terminal-details-table">
              <tbody>
                <tr>
                  <td className="has-text-centered table-data-description">Loading...</td>
                </tr>
              </tbody>
            </table>
          )}

          {!isLoading && filteredTerminalList.length === 0 && (
            <table className="table no-data-table">
              <tbody>
                <tr>
                  <td className="has-text-centered no-data-image-container">
                    <img src={CactusImage} className="no-data-image" />
                  </td>
                </tr>
                <tr>
                  <td className="has-text-centered no-data-text-container">
                    <span className="title">No data to display</span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
        {updateTerminalDetailsEnabled ? (
          <RevampAccordion>
            {filteredTerminalList.slice(0, displayedAmount).map((terminal, index) => {
              return (
                <RevampAccordionItem
                  key={index}
                  title={terminal.terminalType}
                  index={`${terminal.terminal}-${index}`}
                  pill={
                    <StatusSpan
                      status={terminal.status}
                      onExpand={() =>
                        TealiumEvents.terminalExpandsOrCollapsesTab(true, terminal.name)
                      }
                    />
                  }
                >
                  <TerminalDetailsForm terminal={terminal} />
                </RevampAccordionItem>
              );
            })}
          </RevampAccordion>
        ) : (
          <div>
            <Card stretchVertical={true} className="card-table is-hidden-mobile">
              <table className="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Terminal</th>
                    <th>Terminal name</th>
                    <th>Terminal ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTerminalList.slice(0, displayedAmount).map((terminal, index) => {
                    return (
                      <tr key={`terminal-${index}`}>
                        <td automationid={`Terminals_TD_TerminalType_${index}`}>
                          {terminal.terminalType}
                        </td>
                        <td automationid={`Terminals_TD_TerminalName_${index}`}>
                          {terminal.terminalName}
                        </td>
                        <td automationid={`Terminals_TD_TerminalId_${index}`}>
                          {terminal.terminalID}
                        </td>
                        <td automationid={`Terminals_TD_TerminalStatus_${index}`}>
                          <StatusSpan status={terminal.status} />
                        </td>
                      </tr>
                    );
                  })}
                  {!isLoading && filteredTerminalList.length === 0 && (
                    <>
                      <tr>
                        <td colSpan="4" className="has-text-centered no-data-image-container">
                          <img src={CactusImage} className="no-data-image" />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="has-text-centered no-data-text-container">
                          <span className="title">No data to display</span>
                        </td>
                      </tr>
                    </>
                  )}
                  {isLoading && (
                    <tr>
                      <td colSpan="4" className="has-text-centered">
                        Loading...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
            {displayedAmount < filteredTerminalList.length && (
              <div
                className="load-more-container"
                onClick={() => {
                  TealiumEvents.terminalsLoadMoreClicked();

                  const newDisplayedAmount = Math.min(
                    displayedAmount + displayIncrements,
                    filteredTerminalList.length,
                  );
                  setDisplayedAmount(newDisplayedAmount);
                }}
              >
                Load more&nbsp;
                <span className="icon icon-menu-chevron-down is-icon-7"></span>
              </div>
            )}
          </div>
        )}
      </HeaderNav>
    </div>
  );
}
